/**
 * Widget Configuration Hooks
 * React Query hooks for widget configuration management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { widgetConfigService } from '../services/widget-config.service';
import type {
  UpdateBrandingDTO,
  UpdateServicesDTO,
  UpdatePricingDTO,
  UpdateBusinessInfoDTO,
  UpdateEmbedSettingsDTO,
} from '../types/widget-config';

const WIDGET_CONFIG_KEY = ['widget-config'] as const;

/**
 * Hook to fetch widget configuration
 */
export function useWidgetConfig() {
  return useQuery({
    queryKey: WIDGET_CONFIG_KEY,
    queryFn: () => widgetConfigService.getConfig(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to update branding settings
 */
export function useUpdateBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (branding: UpdateBrandingDTO) =>
      widgetConfigService.updateBranding(branding),
    onSuccess: (data) => {
      queryClient.setQueryData(WIDGET_CONFIG_KEY, data);
      toast.success('Branding updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update branding');
    },
  });
}

/**
 * Hook to update service offerings
 */
export function useUpdateServices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (services: UpdateServicesDTO) =>
      widgetConfigService.updateServices(services),
    onSuccess: (data) => {
      queryClient.setQueryData(WIDGET_CONFIG_KEY, data);
      toast.success('Services updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update services');
    },
  });
}

/**
 * Hook to update pricing configuration
 */
export function useUpdatePricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pricing: UpdatePricingDTO) =>
      widgetConfigService.updatePricing(pricing),
    onSuccess: (data) => {
      queryClient.setQueryData(WIDGET_CONFIG_KEY, data);
      toast.success('Pricing updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update pricing');
    },
  });
}

/**
 * Hook to update business information
 */
export function useUpdateBusinessInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (businessInfo: UpdateBusinessInfoDTO) =>
      widgetConfigService.updateBusinessInfo(businessInfo),
    onSuccess: (data) => {
      queryClient.setQueryData(WIDGET_CONFIG_KEY, data);
      toast.success('Business information updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update business information');
    },
  });
}

/**
 * Hook to update embed settings
 */
export function useUpdateEmbedSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (embedSettings: UpdateEmbedSettingsDTO) =>
      widgetConfigService.updateEmbedSettings(embedSettings),
    onSuccess: (data) => {
      queryClient.setQueryData(WIDGET_CONFIG_KEY, data);
      toast.success('Embed settings updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update embed settings');
    },
  });
}

/**
 * Hook to upload logo
 */
export function useLogoUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => widgetConfigService.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WIDGET_CONFIG_KEY });
      toast.success('Logo uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload logo');
    },
  });
}

/**
 * Hook to upload hero image
 */
export function useHeroImageUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => widgetConfigService.uploadHeroImage(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WIDGET_CONFIG_KEY });
      toast.success('Hero image uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload hero image');
    },
  });
}

/**
 * Hook to get embed code
 */
export function useEmbedCode() {
  return useQuery({
    queryKey: ['widget-embed-code'],
    queryFn: () => widgetConfigService.getEmbedCode(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
